import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GmailConnectionsTable } from "@/components/GmailConnectionsTable";
import type { components } from "@dolfin/api-admin";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type GmailConnectionStatus = NonNullable<components["schemas"]["GmailConnectionStatus"]>;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The Record type makes this exhaustive: a new GmailConnectionStatus is a compile error.
const STATUS_LABELS: Record<GmailConnectionStatus, string> = {
  Connected: "Connected",
  NeedsReauth: "Needs reauth",
  Revoked: "Revoked",
  Backfilling: "Backfilling",
  Pending: "Pending",
};

const STATUS_VALUES = Object.keys(STATUS_LABELS) as GmailConnectionStatus[];

export function GmailConnectionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [organisationIdInput, setOrganisationIdInput] = useState(
    searchParams.get("organisationId") ?? "",
  );
  const [clientIdInput, setClientIdInput] = useState(searchParams.get("clientId") ?? "");
  const [emailInput, setEmailInput] = useState(searchParams.get("emailAddress") ?? "");

  const debouncedOrganisationId = useDebouncedValue(organisationIdInput.trim());
  const debouncedClientId = useDebouncedValue(clientIdInput.trim());
  const debouncedEmail = useDebouncedValue(emailInput.trim());

  const statusParam = searchParams.get("status");
  const status: GmailConnectionStatus | "any" = STATUS_VALUES.some(
    (s) => s === statusParam,
  )
    ? (statusParam as GmailConnectionStatus)
    : "any";

  useEffect(() => {
    setSearchParams(
      (params) => {
        if (debouncedOrganisationId) {
          params.set("organisationId", debouncedOrganisationId);
        } else {
          params.delete("organisationId");
        }
        if (debouncedClientId) {
          params.set("clientId", debouncedClientId);
        } else {
          params.delete("clientId");
        }
        if (debouncedEmail) {
          params.set("emailAddress", debouncedEmail);
        } else {
          params.delete("emailAddress");
        }
        return params;
      },
      { replace: true },
    );
  }, [debouncedOrganisationId, debouncedClientId, debouncedEmail, setSearchParams]);

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Only apply the ID filters once they're full UUIDs — the API expects uuid format.
  const organisationId = UUID_PATTERN.test(debouncedOrganisationId)
    ? debouncedOrganisationId
    : undefined;
  const clientId = UUID_PATTERN.test(debouncedClientId) ? debouncedClientId : undefined;

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Gmail Connections</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Gmail connections across all organisations, newest first.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by organisation ID…"
          value={organisationIdInput}
          onChange={(e) => setOrganisationIdInput(e.target.value)}
          aria-invalid={!!debouncedOrganisationId && !organisationId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Input
          placeholder="Filter by client ID…"
          value={clientIdInput}
          onChange={(e) => setClientIdInput(e.target.value)}
          aria-invalid={!!debouncedClientId && !clientId}
          spellCheck={false}
          autoComplete="off"
          className="w-80 font-mono text-xs"
        />
        <Input
          placeholder="Filter by email address…"
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          className="w-72"
        />
        <Select
          value={status}
          onValueChange={(v) => updateParam("status", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">All statuses</SelectItem>
            {STATUS_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {STATUS_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <GmailConnectionsTable
        organisationId={organisationId}
        clientId={clientId}
        emailAddress={debouncedEmail || undefined}
        status={status === "any" ? undefined : status}
      />
    </div>
  );
}
